import { useEffect } from 'react';
import { isFullscreenActive } from '../utils/fullscreen';

/**
 * Dedicated browser proctoring: tab switch, copy/paste, Ctrl+C/V shortcuts,
 * right-click, DevTools, screenshot shortcuts, fullscreen exit restore,
 * navigation shortcuts, and window blur.
 *
 * Multi-monitor stays in useExternalMonitor (ProctoringMonitor) to avoid duplicates.
 */
export function useBrowserSecurity(onViolation, options = {}) {
  const {
    enabled = true,
    blockCopyPaste = false,
    onRestoreFullscreen,
    mediaSelectors = '.proctoring-monitor, .coding-exam-selfview, .interview-left-camera, video',
  } = options;

  useEffect(() => {
    if (!enabled) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your session is in progress. Submit before leaving this page.';
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        onViolation?.({
          type: 'tab_switch',
          severity: 'high',
          timestamp: new Date().toISOString(),
          message: 'Candidate switched tabs or minimized window',
        });
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      if (e.target instanceof Element && e.target.closest(`${mediaSelectors}, video`)) {
        return false;
      }
      onViolation?.({
        type: 'right_click_attempt',
        severity: 'medium',
        timestamp: new Date().toISOString(),
        message: 'Right-click is not available during this proctored session.',
      });
      return false;
    };

    const handleCopy = (e) => {
      const allowInEditor = !blockCopyPaste && e.target?.closest?.('.monaco-editor');
      if (!allowInEditor) {
        e.preventDefault();
        onViolation?.({
          type: 'copy_attempt',
          severity: 'medium',
          timestamp: new Date().toISOString(),
          message: 'Copy is not available during this proctored session.',
        });
      }
    };

    const handlePaste = (e) => {
      const allowInEditor = !blockCopyPaste && e.target?.closest?.('.monaco-editor');
      if (!allowInEditor) {
        e.preventDefault();
        onViolation?.({
          type: 'paste_attempt',
          severity: 'medium',
          timestamp: new Date().toISOString(),
          message: 'Paste is not available during this proctored session.',
        });
      }
    };

    const DEVTOOLS_COOLDOWN_MS = 30000;
    let devToolsLastAlert = 0;
    const detectDevTools = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      if (widthThreshold || heightThreshold) {
        const now = Date.now();
        if (now - devToolsLastAlert < DEVTOOLS_COOLDOWN_MS) return;
        devToolsLastAlert = now;
        onViolation?.({
          type: 'devtools_opened',
          severity: 'critical',
          timestamp: new Date().toISOString(),
          message: 'Developer tools detected',
        });
      }
    };

    let hasEnteredFullscreen = isFullscreenActive();
    const handleFullscreenChange = () => {
      if (isFullscreenActive()) {
        hasEnteredFullscreen = true;
        return;
      }
      if (!hasEnteredFullscreen) return;
      onViolation?.({
        type: 'fullscreen_exit',
        severity: 'high',
        timestamp: new Date().toISOString(),
        message: 'Candidate exited fullscreen',
      });
      onRestoreFullscreen?.();
    };

    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      // Cannot reliably cancel Esc fullscreen exit in all browsers; restore after.
      if (onRestoreFullscreen) {
        setTimeout(() => onRestoreFullscreen(), 50);
      }
    };

    const handleNavigationKeys = (e) => {
      const key = e.key.toLowerCase();
      const blocked =
        key === 'f5'
        || ((e.ctrlKey || e.metaKey) && ['w', 't', 'n', 'r'].includes(key))
        || (e.ctrlKey && e.shiftKey && key === 't');
      if (!blocked) return;
      e.preventDefault();
      onViolation?.({
        type: 'shortcut_attempt',
        severity: 'medium',
        timestamp: new Date().toISOString(),
        message: 'You cannot leave or refresh until you submit.',
      });
    };

    const preventScreenshot = (e) => {
      if (
        e.key === 'PrintScreen'
        || (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key))
      ) {
        e.preventDefault();
        onViolation?.({
          type: 'screenshot_attempt',
          severity: 'high',
          timestamp: new Date().toISOString(),
          message: 'Screenshot attempt detected',
        });
      }
    };

    const BLUR_COOLDOWN_MS = 5000;
    const BLUR_GRACE_MS = 350;
    let blurLastAlert = 0;
    let blurGraceTimer = null;

    const isProctoringMediaInteraction = () => {
      const active = document.activeElement;
      if (active instanceof Element && active.closest(`${mediaSelectors}, video`)) {
        return true;
      }
      return Boolean(document.pictureInPictureElement);
    };

    const handleBlur = () => {
      if (blurGraceTimer) clearTimeout(blurGraceTimer);
      blurGraceTimer = setTimeout(() => {
        blurGraceTimer = null;
        if (document.hasFocus()) return;
        if (isProctoringMediaInteraction() || document.hidden) return;
        const now = Date.now();
        if (now - blurLastAlert < BLUR_COOLDOWN_MS) return;
        blurLastAlert = now;
        onViolation?.({
          type: 'window_blur',
          severity: 'medium',
          timestamp: new Date().toISOString(),
          message: 'Window lost focus',
        });
      }, BLUR_GRACE_MS);
    };

    const handleWindowFocus = () => {
      if (blurGraceTimer) {
        clearTimeout(blurGraceTimer);
        blurGraceTimer = null;
      }
    };

    const handleKeyboardShortcuts = (e) => {
      const allowInEditor = !blockCopyPaste && e.target?.closest?.('.monaco-editor');
      if ((e.ctrlKey || e.metaKey) && !allowInEditor) {
        const key = e.key.toLowerCase();
        const blockedKeys = ['c', 'v', 'a', 'f', 'x'];
        if (blockedKeys.includes(key)) {
          e.preventDefault();
          const type = key === 'c' ? 'copy_attempt' : key === 'v' ? 'paste_attempt' : 'shortcut_attempt';
          const message = key === 'c'
            ? 'Copy is not available during this proctored session.'
            : key === 'v'
              ? 'Paste is not available during this proctored session.'
              : key === 'x'
                ? 'Cut is not available during this proctored session.'
                : `Ctrl+${e.key.toUpperCase()} is not available during this proctored session.`;
          onViolation?.({
            type,
            severity: 'medium',
            timestamp: new Date().toISOString(),
            message,
          });
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('keydown', handleNavigationKeys);
    document.addEventListener('keydown', preventScreenshot);
    document.addEventListener('keydown', handleKeyboardShortcuts);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleWindowFocus);

    const devToolsInterval = setInterval(detectDevTools, 1000);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      document.removeEventListener('keydown', handleEscape, true);
      document.removeEventListener('keydown', handleNavigationKeys);
      document.removeEventListener('keydown', preventScreenshot);
      document.removeEventListener('keydown', handleKeyboardShortcuts);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleWindowFocus);
      if (blurGraceTimer) clearTimeout(blurGraceTimer);
      clearInterval(devToolsInterval);
    };
  }, [onViolation, enabled, blockCopyPaste, onRestoreFullscreen, mediaSelectors]);
}

export default useBrowserSecurity;
